import { MdGridView, MdListAlt, MdDehaze } from 'react-icons/md';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MiniStatistics from 'components/card/MiniStatistics';

import Card from 'components/card';
import { getPreparations } from 'interfaces/orderPreparation';
import { IOrderPreparation, IPreparationLine } from 'interfaces/orderPreparationTypes';
import PreparationCard from 'components/card/preparationCard';
import SearchIcon from 'components/icons/SearchIcon';
import { FaArrowRotateRight } from 'react-icons/fa6';
import { getUsersLight } from 'interfaces/user';

const ListPreparations = () => {
    const nav = useNavigate();
    const [preparations, setPreparations] = useState<IOrderPreparation[] | null>(null);
    const [filteredPreparations, setFilteredPreparations] = useState<IOrderPreparation[] | null>(null);
    const [search, setSearch] = useState('');
    const [view, setView] = useState('grid');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [dateFilter, setDateFilter] = useState<string>('all'); // 'all', 'today', 'week', 'month'
    const [preparerFilter, setPreparerFilter] = useState<string>('all');
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const statusLabels = {
        all: 'Toutes',
        draft: 'Brouillon',
        in_progress: 'En cours',
        completed: 'Terminées',
        cancelled: 'Annulées'
    };

    const dateLabels = {
        all: 'Toutes dates',
        today: "Aujourd'hui",
        week: 'Cette semaine',
        month: 'Ce mois'
    };

    const fetchPreparations = async () => {
        setLoading(true);
        const filters = statusFilter !== 'all' ? { status: statusFilter } : undefined;
        const res = await getPreparations(filters);
        if (res) {
            setPreparations(res);
            setFilteredPreparations(res);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchPreparations();
        
        // Fetch users for preparer filter
        (async () => {
            const u = await getUsersLight();
            if (u) setUsers(u);
        })();
    }, [statusFilter]);

    useEffect(() => {
        if (!preparations) return;
        
        let filtered = [...preparations];
        
        // Filter by search
        if (search.trim() !== '') {
            filtered = filtered.filter((prep) => 
                prep.orderNumber.toLowerCase().includes(search.toLowerCase()) ||
                prep.preparationLines.some((line: IPreparationLine) => 
                    line.productName.toLowerCase().includes(search.toLowerCase())
                )
            );
        }
        
        // Filter by date
        if (dateFilter !== 'all') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            
            filtered = filtered.filter((prep) => {
                const prepDate = new Date(prep.createdAt);
                
                if (dateFilter === 'today') {
                    return prepDate >= today;
                } else if (dateFilter === 'week') {
                    const weekAgo = new Date(today);
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return prepDate >= weekAgo;
                } else if (dateFilter === 'month') {
                    const monthAgo = new Date(today);
                    monthAgo.setMonth(monthAgo.getMonth() - 1);
                    return prepDate >= monthAgo;
                }
                return true;
            });
        }
        
        // Filter by preparer
        if (preparerFilter !== 'all') {
            filtered = filtered.filter((prep) => {
                // Check if preparedBy exists and matches
                if (!prep.preparedBy) return false;
                
                // Handle both object format {_id, fullName} and string format
                const preparerId = typeof prep.preparedBy === 'object' && prep.preparedBy._id 
                    ? prep.preparedBy._id 
                    : prep.preparedBy;
                
                return preparerId === preparerFilter;
            });
        }
        
        setFilteredPreparations(filtered);
    }, [search, preparations, dateFilter, preparerFilter]);

    const stats = {
        total: preparations?.length || 0,
        draft: preparations?.filter(p => p.status === 'draft').length || 0,
        in_progress: preparations?.filter(p => p.status === 'in_progress').length || 0,
        completed: preparations?.filter(p => p.status === 'completed').length || 0,
    };

    return (
        <div className="mt-4 grid h-full w-full grid-cols-1 gap-5 xl:mt-3">
            {/* Stats Cards */}
            <div className="z-0 col-span-12 grid h-full w-full grid-cols-2 gap-3 sm:gap-5 md:grid-cols-2 xl:grid-cols-4">
                <MiniStatistics
                    name="Total"
                    value={stats.total.toString()}
                    icon={<MdListAlt color="#0260CB" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Brouillon"
                    value={stats.draft.toString()}
                    icon={<MdListAlt color="#0260CB" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="En cours"
                    value={stats.in_progress.toString()}
                    icon={<MdListAlt color="#0260CB" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Terminées"
                    value={stats.completed.toString()}
                    icon={<MdListAlt color="#0260CB" />}
                    iconBg="bg-lightPrimary"
                />
            </div>

            {/* Main Card */}
            <div className="col-span-12 h-full w-full rounded-[20px]">
                <Card extra="pt-[10px] pl-[20px] pr-[20px] pb-[20px] border">
                    {/* Status Tabs */}
                    <div className="w-full flex items-center m-auto">
                        <div className="mt-2 flex w-full gap-[2px] overflow-hidden border-b border-gray-200 justify-center flex-row">
                            {Object.entries(statusLabels).map(([key, label]) => (
                                <div
                                    key={key}
                                    className={
                                        statusFilter === key
                                            ? 'border-b-[4px] border-[#0260CB] pb-[8px] pt-[8px] hover:cursor-default dark:border-brand-400 bg-[#7090B0]/[0.05] flex items-center justify-center gap-2 rounded-t-[15px] w-1/5 h-[40px] text-center'
                                            : 'pb-[11px] pt-[8px] hover:cursor-pointer bg-[#7090B0]/[0.15] flex items-center justify-center gap-2 rounded-t-[15px] w-1/5 h-[40px] text-center'
                                    }
                                    onClick={() => setStatusFilter(key)}
                                >
                                    <p className="text-xs font-medium text-navy-700 dark:text-white md:text-[15px]">
                                        {label}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Search */}
                    <div className="mt-[20px] flex flex-col sm:flex-row justify-between items-center gap-y-[10px]">
                        <div className="w-full flex gap-5">
                            <div className="flex h-[38px] w-full items-center rounded-xl bg-lightPrimary text-sm text-gray-600 sm:w-[40%]">
                                <SearchIcon />
                                <input
                                    type="text"
                                    placeholder="Rechercher par commande ou produit..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                                />
                            </div>
                            <div className="mt-1">
                                <select
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                    className="h-[38px] rounded-xl bg-lightPrimary px-3 text-sm text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                                >
                                    {Object.entries(dateLabels).map(([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="mt-1">
                                <select
                                    value={preparerFilter}
                                    onChange={(e) => setPreparerFilter(e.target.value)}
                                    className="h-[38px] rounded-xl bg-lightPrimary px-3 text-sm text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                                >
                                    <option value="all">Tous les préparateurs</option>
                                    {users.map((user: any) => (
                                        <option key={user._id} value={user._id}>
                                            {user.fullName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-2 items-center">
                            <button
                                onClick={fetchPreparations}
                                className="flex items-center justify-center rounded-xl bg-lightPrimary p-2 text-[#0260CB] transition duration-200 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10"
                            >
                                <FaArrowRotateRight className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* View Toggle (desktop) */}
                    <div className="hidden lg:block absolute left-6 top-6">
                        <div className="flex gap-2">
                            <MdGridView
                                size={20}
                                className={`${
                                    view === 'grid' && 'text-[#0260CB]'
                                } cursor-pointer`}
                                onClick={() => setView('grid')}
                            />
                            <MdDehaze
                                size={20}
                                className={`${
                                    view === 'list' && 'text-[#0260CB]'
                                } cursor-pointer`}
                                onClick={() => setView('list')}
                            />
                        </div>
                    </div>

                    {/* Content */}
                    <div className={`mt-2 grid grid-cols-1 gap-2 ${
                        view === 'grid' && 'gap-5 md:grid-cols-2 2xl:grid-cols-4'
                    }`}>
                        {loading ? (
                            <div className="col-span-12 flex h-[400px] items-center justify-center">
                                <div className="text-center">
                                    <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#0260CB] border-r-transparent"></div>
                                    <p className="text-gray-600">Chargement des préparations...</p>
                                </div>
                            </div>
                        ) : filteredPreparations && filteredPreparations.length > 0 ? (
                            filteredPreparations.map((preparation) => (
                                <PreparationCard
                                    key={preparation._id}
                                    preparation={preparation}
                                />
                            ))
                        ) : (
                            <div className="col-span-12 flex h-[400px] items-center justify-center">
                                <div className="text-center">
                                    <p className="text-xl font-semibold text-gray-600">
                                        Aucune préparation trouvée
                                    </p>
                                    <p className="mt-2 text-sm text-gray-400">
                                        {search ? 'Essayez avec d\'autres mots-clés' : 'Créez une préparation depuis une commande'}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default ListPreparations;
